import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, RotateCcwIcon } from 'lucide-react';
import { AttractScene } from './AttractScene';
import { InteractScene } from './InteractScene';
import { RevealScene } from './RevealScene';
import { ExplainScene } from './ExplainScene';
import { ContinueScene } from './ContinueScene';
import { conceptDisclaimer } from '../../data/caprelsa';
import type { ConsiderationId } from '../../data/caprelsa';
type CaprelsaSnakProps = {
  /** Scene to open on: 0 Attract · 1 Interact · 2 Reveal · 3 Explain · 4 Continue. */
  initialScreen?: number;
  /** Render as a static still with seeded state. */
  still?: boolean;
};
export const SNAK_SCREENS = 5;
const EASE = [0.23, 1, 0.32, 1] as const;

/** The ~40-second Caprelsa® Snak: Ask → Interact → Reveal → Explain → Continue. Built for a 390px-wide phone screen. */
export function CaprelsaSnak({
  initialScreen = 0,
  still = false
}: CaprelsaSnakProps) {
  const reduced = useReducedMotion() ?? false;
  const [screen, setScreen] = useState(initialScreen);
  const [active, setActive] = useState<ConsiderationId | null>(still ? 'progression' : null);
  const [explored, setExplored] = useState<ConsiderationId[]>(still ? ['progression', 'ret'] : []);
  const [inhibited, setInhibited] = useState(still);
  const next = () => setScreen(s => Math.min(SNAK_SCREENS - 1, s + 1));
  const back = () => setScreen(s => Math.max(0, s - 1));
  const replay = () => {
    setActive(null);
    setExplored([]);
    setInhibited(false);
    setScreen(0);
  };
  const select = (id: ConsiderationId) => {
    setActive(id);
    setExplored(e => e.includes(id) ? e : [...e, id]);
  };
  return <section aria-label="Caprelsa interactive Snak" className="relative flex h-full flex-1 flex-col overflow-hidden bg-white px-6 pb-4 pt-4 font-sans text-cap-ink" data-id="element-404" data-mp="1whywhn8ubuqf">
      <div className="flex items-center gap-3" data-id="element-405" data-mp="l3lbcq5h1te3">
        <button type="button" onClick={back} disabled={screen === 0} aria-label="Previous scene" className="-ml-2 rounded-full p-1.5 text-cap-purple/60 transition-[color,opacity] duration-150 hover:text-cap-purple disabled:opacity-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cap-gold" data-id="element-406" data-mp="l5fx26okhpj8">
          <ChevronLeftIcon size={18} strokeWidth={2} data-id="element-407" data-mp="3jgj6k18n16p7" />
        </button>
        <div className="flex flex-1 gap-1.5" role="progressbar" aria-valuemin={1} aria-valuemax={SNAK_SCREENS} aria-valuenow={screen + 1} data-id="element-408" data-mp="3egn446fl9nbr">
          {Array.from({
          length: SNAK_SCREENS
        }).map((_, i) => <span key={i} className="h-[3px] flex-1 overflow-hidden rounded-full bg-cap-line" data-id="element-409" data-mp="35fowle5xyxof" data-mpi={i}>
              <motion.span className="block h-full origin-left bg-cap-gold" initial={false} animate={{
            scaleX: i <= screen ? 1 : 0
          }} transition={{
            duration: 0.25,
            ease: EASE
          }} data-id="element-410" data-mp="30h59hafm3fuh" />
            </span>)}
        </div>
        <span className="w-7 text-right font-mono text-[10px] tracking-[0.1em] text-cap-grey" data-id="element-411" data-mp="169b6o9ykxrwv">
          {screen + 1}/{SNAK_SCREENS}
        </span>
        <button type="button" onClick={replay} disabled={screen === 0} aria-label="Replay from the start" title="Replay" className="-mr-2 rounded-full p-1.5 text-cap-purple/60 transition-[color,opacity] duration-150 hover:text-cap-purple disabled:opacity-30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cap-gold" data-id="element-412" data-mp="1b6jr6h77a81a">
          <RotateCcwIcon size={16} strokeWidth={2} data-id="element-413" data-mp="s9s8o9zhfwya" />
        </button>
      </div>

      <div className="relative mt-4 flex flex-1 flex-col" data-id="element-414" data-mp="x7k2j9jott54">
        <AnimatePresence mode="wait" initial={false} data-id="element-415" data-mp="119ov04yegjq6">
          <motion.div key={screen} className="flex flex-1 flex-col [&>*]:flex-1" initial={{
          opacity: 0,
          x: reduced ? 0 : 16
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: reduced ? 0 : -16
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} data-id="element-416" data-mp="11bhzpepgctvj">
            {screen === 0 && <AttractScene onNext={next} still={still} data-id="element-417" data-mp="2693ke0334c5b" />}
            {screen === 1 && <InteractScene active={active} explored={explored} onSelect={select} onNext={next} data-id="element-418" data-mp="26b7x97iyolhq" />}
            {screen === 2 && <RevealScene onNext={next} still={still} data-id="element-419" data-mp="2g717uld4e4wv" />}
            {screen === 3 && <ExplainScene inhibited={inhibited} onToggle={() => setInhibited(v => !v)} onNext={next} still={still} data-id="element-420" data-mp="2g9477w05xae9" />}
            {screen === 4 && <ContinueScene onMechanism={() => setScreen(3)} onReplay={replay} still={still} data-id="element-421" data-mp="2p7zfreqoiwkq" />}
          </motion.div>
        </AnimatePresence>
      </div>

      <p className="mt-3 text-center text-[9px] leading-snug text-cap-grey/80" data-id="element-422" data-mp="33vu4yl5hait2">{conceptDisclaimer}</p>
    </section>;
}